import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Card,
  CardHeader,
  CardContent,
  Button,
  IconButton,
} from '@mui/material';
import parse from 'html-react-parser';
import { useWrittenDirectivesContext } from '../context';
import { neutral } from '_lex/_foundations/phxColors';
import { useParams } from 'react-router-dom';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
import RightDrawer from '_lex/_layouts/TakeoverLayout/RightDrawer';
import { useStandardsContext } from '../../context';
import { translate as t } from 'hooks/useTranslations';
import useAccreditationStandardById from '../../../../_data_sources/AccreditationStandards/useStandard';
import { getIssuedManual } from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManual';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import WrittenDirectivePolicy from '../_components/WrittenDirectivePolicy';
import WrittenDirectivePreface from '../_components/WrittenDirectivePreface';
import { QuickSearch } from 'components/_atoms';
import { useNavigate } from 'react-router-dom';
import Loading from 'views/App/Portal/Loading';
import { Search, Delete } from '@mui/icons-material';
import { useConfirm } from 'material-ui-confirm';
import { destroyWrittenDirective } from '../../../../_data_sources/WrittenDirectives/manageWrittenDirectives';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import { AppContext } from 'views/App/context';

const WrittenDirectiveDetail: React.FC = () => {
  const { directiveId, id: accreditationId, versionId, standardId } = useParams();
  const { currentOrganizationId, isFeatureEnabled, hasPermission } = useContext(AppContext);
  const useRUIData = isFeatureEnabled('awbRUIData');
  const { writtenDirectives, isLoading: isDirectivesLoading, jurisdiction } = useWrittenDirectivesContext();
  const { version, isLoading: isVersionLoading } = useStandardsContext();
  const [manual, setManual] = useState<Manual | null>(null);
  const [content, setContent] = useState<any | null>(null);
  const [contentType, setContentType] = useState<'policy' | 'preface' | null>(null);
  const [contentId, setContentId] = useState<string | null>(null);
  const [removingDirectiveId, setRemovingDirectiveId] = useState<string | null>(null);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data: standard, isLoading: isStandardLoading } = useAccreditationStandardById(
    currentOrganizationId,
    accreditationId || '',
    versionId || '',
    standardId || ''
  );
  const navigate = useNavigate();

  const directive = writtenDirectives?.find((d) => d.id === directiveId);
  const standardTitle = standard ? `${standard.heading} ${standard.title}` : '';

  useEffect(() => {
    const fetchManualContent = async () => {
      if (!directive?.contentUri) return;

      // Extract manualId and contentId from contentUri
      const manualId = directive.contentUri.match(/\/manual[s]?\/([^/]+)\/content\/([^/]+)/)?.[1];
      const extractedContentId = directive.contentUri.match(/\/manual[s]?\/([^/]+)\/content\/([^/]+)/)?.[2];

      if (!manualId || !extractedContentId) return;

      setContentId(extractedContentId);

      try {
        const manualData = await getIssuedManual(manualId);
        setManual(manualData);

        // Search in prefaces first
        const preface = manualData.prefaces?.find((p) => p.id === extractedContentId);
        if (preface) {
          setContent(preface);
          setContentType('preface');
          return;
        }

        // Search in chapters and policies
        for (const chapter of manualData.chapters || []) {
          for (const policy of chapter.policies || []) {
            if (policy.id === extractedContentId) {
              setContent(policy);
              setContentType('policy');
              return;
            }
            // Check policy sections
            for (const section of policy.sections || []) {
              if (section.id === extractedContentId) {
                setContent(policy);
                setContentType('policy');
                return;
              }
              // Check subsections
              for (const subsection of section.subSections || []) {
                if (subsection.id === extractedContentId) {
                  setContent(policy);
                  setContentType('policy');
                  return;
                }
              }
            }
          }
        }
      } catch (error) {
        console.error('Error fetching manual content:', error);
      }
    };

    fetchManualContent();
  }, [directive]);

  const isLoading = isDirectivesLoading || isVersionLoading || isStandardLoading;

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (!directive) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography
            variant='bodySm'
            color='text.secondary'
          >
            Written directive not found
          </Typography>
        </Box>
      );
    }

    // Show loading state while fetching manual content
    if (directive?.contentUri && !manual) {
      return <Loading />;
    }

    if (!manual || !content || !contentType || !contentId) {
      return (
        <Box sx={{ py: 2 }}>
          <Typography
            variant='bodySm'
            color='text.secondary'
          >
            Content not found
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        className='written-directive-detail'
        sx={{ width: '100%' }}
      >
        {contentType === 'policy' ? (
          <WrittenDirectivePolicy
            manualId={manual.id}
            policy={content}
            scrollId={contentId}
            manual={manual}
          />
        ) : (
          <WrittenDirectivePreface
            manualId={manual.id}
            preface={content}
            scrollId={contentId}
            manual={manual}
          />
        )}
      </Box>
    );
  };

  const renderRightDrawer = () => {
    if (!directive) return null;

    return (
      <RightDrawer
        header={t('awb.standards.writtenDirectives.rightDrawer.title')}
        noPadding
        content={
          <Stack spacing={0}>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.jurisdiction')} />
              <CardContent>
                <Typography variant='bodySm'>{jurisdiction?.name || '-'}</Typography>
              </CardContent>
            </Card>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.writtenDirectives')} />
              <CardContent>
                <Stack spacing={2}>
                  {writtenDirectives?.map((directive) => (
                    <Box
                      key={directive.id}
                      sx={{
                        py: 1,
                        borderBottom: `1px solid ${neutral[100]}`,
                        '&:last-child': {
                          borderBottom: 'none',
                        },
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, width: 0 }}>
                        <Typography
                          variant='labelSm'
                          noWrap
                          sx={{
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                            mb: 0.5,
                            display: 'block',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                          onClick={() =>
                            navigate(
                              `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${jurisdiction?.id}/written-directives/${directive.id}`
                            )
                          }
                        >
                          {directive.contentHeading} {directive.contentTitle}
                        </Typography>
                        <Typography
                          variant='bodySm'
                          noWrap
                          sx={{
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {directive.manualTitle}
                        </Typography>
                      </Box>
                      {!isLoading && contentType && (
                        <IconButton
                          size='small'
                          onClick={() => handleRemoveWrittenDirectives(directive.id)}
                          disabled={
                            removingDirectiveId === directive.id ||
                            !hasPermission('deleteWrittenDirective', currentOrganizationId)
                          }
                          sx={{ ml: 1, color: neutral[500] }}
                        >
                          {removingDirectiveId === directive.id ? (
                            <CircularProgress size={16} />
                          ) : (
                            <Delete fontSize='small' />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            <Card className='lex-drawer-card'>
              <CardHeader title={t('awb.standards.writtenDirectives.rightDrawer.description')} />
              <CardContent>
                <Box sx={{ maxHeight: '300px', overflow: 'auto', minWidth: 0, whiteSpace: 'normal' }}>
                  <Typography
                    component='div'
                    variant='bodySm'
                  >
                    {parse(standard?.draftContent || '-')}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        }
        open={true}
      />
    );
  };

  const handleRemoveWrittenDirectives = (directiveIdToRemove: string) => {
    confirm({
      title: t('awb.standards.writtenDirectives.removeModal.title'),
      description: t('awb.standards.writtenDirectives.removeModal.description'),
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('awb.standards.writtenDirectives.removeModal.confirmText'),
      cancellationText: t('awb.standards.writtenDirectives.removeModal.cancelText'),
    })
      .then(() => {
        if (!currentOrganizationId || !accreditationId || !versionId || !standardId || !directiveIdToRemove) {
          console.error('Invalid request when deleting written directive');
          return;
        }

        setRemovingDirectiveId(directiveIdToRemove);

        destroyWrittenDirective(
          currentOrganizationId,
          accreditationId,
          versionId,
          standardId,
          directiveIdToRemove,
          () => {
            setRemovingDirectiveId(null);
            enqueueSnackbar('Written directive removed successfully', {
              variant: 'success',
              autoHideDuration: 6000,
            });

            queryClient.invalidateQueries(['written_directives']);

            navigate(
              `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${jurisdiction?.id}/written-directives`
            );
          },
          (error: any) => {
            setRemovingDirectiveId(null);
            enqueueSnackbar(error.message || 'Failed to remove written directive', {
              variant: 'error',
              autoHideDuration: 10000,
            });
          },
          useRUIData
        );
      })
      .catch(() => {
        // User cancelled, do nothing
      });
  };

  return (
    <TakeoverLayout
      pageTitle={isLoading ? '' : `${standardTitle}: Written Directives`}
      backUrl={`/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}`}
      rightDrawer={renderRightDrawer()}
      contentHeaderLeft={
        <Button
          variant='outlined'
          color='neutral'
          size='small'
          startIcon={<Search />}
          onClick={() =>
            navigate(
              `/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}/jurisdiction/${jurisdiction?.id}/written-directives`
            )
          }
        >
          {t('awb.standards.writtenDirectives.listButtonText')}
        </Button>
      }
      contentHeaderRight={
        <Stack
          direction='row'
          spacing={2}
        >
          <Button
            variant='contained'
            size='small'
            onClick={() =>
              navigate(`/admin/accreditation/${accreditationId}/versions/${versionId}/standards/${standardId}`)
            }
          >
            {t('awb.standards.writtenDirectives.actions.done')}
          </Button>
        </Stack>
      }
      contentBackground='neutral'
    >
      <Box sx={{ display: 'flex' }}>{renderContent()}</Box>
    </TakeoverLayout>
  );
};

export default WrittenDirectiveDetail;
